"use client";

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import Image from "next/image";
import { Upload, Trash2, Edit3, X, Check, Loader2, Search } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";

import {
  CloudinaryImage,
  MediaDialogProps,
} from "@/types/cloudinary/cloudinary";

// Import file type logos
import {
  PDFLogo,
  PhotoshopLogo,
  IllustratorLogo,
  InDesignLogo,
  WordLogo,
  ExcelLogo,
  PowerPointLogo,
} from "@/components/assets/logos";

// Helper function to get the appropriate logo component for a file type
const getFileTypeLogo = (format: string) => {
  const fileFormat = format?.toLowerCase();

  switch (fileFormat) {
    case "pdf":
      return PDFLogo;
    case "psd":
      return PhotoshopLogo;
    case "ai":
    case "eps":
      return IllustratorLogo;
    case "indd":
      return InDesignLogo;
    case "doc":
    case "docx":
      return WordLogo;
    case "xls":
    case "xlsx":
      return ExcelLogo;
    case "ppt":
    case "pptx":
      return PowerPointLogo;
    default:
      return null;
  }
};

// Helper function to check if a file format is an image
const isImageFormat = (format: string) => {
  const imageFormats = [
    "jpg",
    "jpeg",
    "png",
    "gif",
    "webp",
    "svg",
    "bmp",
    "ico",
  ];
  return imageFormats.includes(format?.toLowerCase() || "");
};

export function MediaDialog({
  isOpen,
  onClose,
  initialImages,
  initialCursor,
  initialHasMore,
  preSelectedImages = [],
  onSelect,
  mediaSources,
  uploadDestinations,
  onLoadMore,
  onSearch,
  onUpload,
  onDelete,
  onRename,
  folder = "products",
  showSelectedDisplay = true,
  maxSelection,
  allowedFileTypes,
  excludeFileTypes,
}: MediaDialogProps) {
  const [images, setImages] = useState<CloudinaryImage[]>([]);
  const [selectedImages, setSelectedImages] = useState<CloudinaryImage[]>(
    preSelectedImages || []
  );
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(initialHasMore);
  const [nextCursor, setNextCursor] = useState(initialCursor);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [isDragOver, setIsDragOver] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [currentSourceIndex] = useState(0); // For future multi-source UI (setter unused for now)
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);
  const [userHasInteracted, setUserHasInteracted] = useState(false);

  // Drag and drop state for reordering
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Use refs for values that don't need to trigger re-renders
  const allowedFileTypesRef = useRef(allowedFileTypes);
  const excludeFileTypesRef = useRef(excludeFileTypes);
  const initialImagesRef = useRef(initialImages);
  const initialCursorRef = useRef(initialCursor);
  const initialHasMoreRef = useRef(initialHasMore);
  const preSelectedImagesRef = useRef(preSelectedImages);

  // Update refs when props change
  useEffect(() => {
    allowedFileTypesRef.current = allowedFileTypes;
    excludeFileTypesRef.current = excludeFileTypes;
    initialImagesRef.current = initialImages;
    initialCursorRef.current = initialCursor;
    initialHasMoreRef.current = initialHasMore;
    preSelectedImagesRef.current = preSelectedImages;
  });

  // Memoized filter function to avoid recreating on every render
  const filterImages = useMemo(() => {
    return (imagesToFilter: CloudinaryImage[]) => {
      const allowed = allowedFileTypesRef.current;
      const excluded = excludeFileTypesRef.current;

      return imagesToFilter.filter((image) => {
        const fileFormat = image.format?.toLowerCase();
        if (allowed && allowed.length > 0) {
          return allowed.includes(fileFormat);
        }
        if (excluded && excluded.length > 0) {
          return !excluded.includes(fileFormat);
        }
        return true;
      });
    };
  }, []);

  // Create combined configuration from either new or legacy props (memoized)
  const isUsingNewConfig = useMemo(
    () => mediaSources && mediaSources.length > 0,
    [mediaSources]
  );

  // Create legacy-compatible source if using old props (memoized)
  const legacySource = useMemo(
    () =>
      !isUsingNewConfig
        ? {
            id: "legacy",
            name: folder || "Media",
            folder: folder || "products",
            allowedFileTypes,
            excludeFileTypes,
            onLoadMore: onLoadMore!,
            onSearch: onSearch!,
          }
        : null,
    [
      isUsingNewConfig,
      folder,
      allowedFileTypes,
      excludeFileTypes,
      onLoadMore,
      onSearch,
    ]
  );

  const allSources = useMemo(
    () => (isUsingNewConfig ? mediaSources! : [legacySource!]),
    [isUsingNewConfig, mediaSources, legacySource]
  );
  const currentSource = useMemo(
    () => allSources[currentSourceIndex] || allSources[0],
    [allSources, currentSourceIndex]
  );

  // Create legacy-compatible upload destinations if using old props (memoized)
  const legacyUploadDestination = useMemo(
    () =>
      !isUsingNewConfig && onUpload
        ? [
            {
              id: "legacy",
              name: folder || "Default",
              folder: folder || "products",
              fileTypes: ["*"], // Accept all file types
              onUpload: onUpload,
            },
          ]
        : [],
    [isUsingNewConfig, onUpload, folder]
  );

  const allUploadDestinations = useMemo(
    () => uploadDestinations || legacyUploadDestination,
    [uploadDestinations, legacyUploadDestination]
  );

  // Function to get the appropriate upload handler for a file
  const getUploadHandlerForFile = useCallback(
    (file: File) => {
      const fileExtension = file.name.split(".").pop()?.toLowerCase() || "";

      // Find matching upload destination
      const destination = allUploadDestinations.find(
        (dest) =>
          dest.fileTypes.includes("*") || dest.fileTypes.includes(fileExtension)
      );

      return destination?.onUpload;
    },
    [allUploadDestinations]
  );

  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Reset state when dialog opens with new data
  useEffect(() => {
    if (isOpen) {
      console.log("🔍 MediaDialog opening with data:", {
        folder,
        initialImagesCount: initialImagesRef.current.length,
        allowedFileTypes: allowedFileTypesRef.current,
        excludeFileTypes: excludeFileTypesRef.current,
      });

      const filteredImages = filterImages(initialImagesRef.current);

      console.log("🔍 After filtering:", {
        originalCount: initialImagesRef.current.length,
        filteredCount: filteredImages.length,
        firstFewImages: filteredImages.slice(0, 3).map((img) => ({
          public_id: img.public_id,
          format: img.format,
          display_name: img.display_name,
        })),
      });

      setImages(filteredImages);
      setHasMore(initialHasMoreRef.current);
      setNextCursor(initialCursorRef.current);
      setSelectedImages(preSelectedImagesRef.current || []);
      setEditingId(null);
      setSearchTerm("");
      setIsSearching(false);
      setHasAutoLoaded(false);
      setUserHasInteracted(false);
    }
  }, [isOpen, filterImages, folder]);

  // Update selected images when preSelectedImages changes (only when dialog opens or when explicitly set)
  useEffect(() => {
    // Only update preselected images when the dialog opens, not during user interaction
    if (!isOpen) return;

    // Don't override user selections
    if (userHasInteracted) {
      return;
    }

    const newPreSelected = preSelectedImagesRef.current || [];

    // Only set preselected images once when dialog opens, don't interfere with user selections
    setSelectedImages(newPreSelected);
  }, [isOpen, userHasInteracted]);

  // Auto-load files when dialog opens with empty initial data
  useEffect(() => {
    if (
      isOpen &&
      initialImagesRef.current.length === 0 &&
      currentSource?.onLoadMore &&
      !hasAutoLoaded
    ) {
      // Automatically load files if we start with empty data
      const autoLoad = async () => {
        try {
          setIsLoadingMore(true);
          setHasAutoLoaded(true); // Mark as auto-loaded
          const result = await currentSource.onLoadMore(undefined, "");

          const filteredNewImages = filterImages(result.images);

          setImages(filteredNewImages);
          setNextCursor(result.nextCursor);
          setHasMore(result.hasMore);
        } catch (error) {
          console.error("Auto-load error:", error);
          // Show user-friendly error message
          toast({
            variant: "destructive",
            description: excludeFileTypesRef.current?.includes("jpg")
              ? "Failed to load files"
              : "Failed to load images",
          });
        } finally {
          setIsLoadingMore(false);
        }
      };

      autoLoad();
    }
  }, [isOpen, currentSource, hasAutoLoaded, filterImages]);

  // Debounced search effect
  useEffect(() => {
    if (!isOpen) return;

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // If search term is empty, don't search
    if (!searchTerm.trim()) {
      return;
    }

    // Set new timeout for search
    searchTimeoutRef.current = setTimeout(async () => {
      if (!currentSource?.onSearch) return;

      setIsSearching(true);
      try {
        const result = await currentSource.onSearch(searchTerm);

        const filteredResults = filterImages(result.images);

        setImages(filteredResults);
        setNextCursor(result.nextCursor);
        setHasMore(result.hasMore);

        // Only reset to preselected if user hasn't made their own selections
        if (!userHasInteracted) {
          // Maintain pre-selected images that are found in filtered search results
          const foundPreSelected = (preSelectedImagesRef.current || []).filter(
            (preSelected) =>
              filteredResults.some(
                (img) => img.public_id === preSelected.public_id
              )
          );
          setSelectedImages(foundPreSelected);
        } else {
          // Keep current user selections that are still available in search results
          setSelectedImages((prev) =>
            prev.filter((selected) =>
              filteredResults.some(
                (img) => img.public_id === selected.public_id
              )
            )
          );
        }
      } catch (error) {
        console.error("Search error:", error);
        // Show user-friendly error message
        toast({
          variant: "destructive",
          description: excludeFileTypesRef.current?.includes("jpg")
            ? "Failed to search files"
            : "Failed to search images",
        });
      } finally {
        setIsSearching(false);
      }
    }, 500); // 500ms delay

    // Cleanup function
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, isOpen, currentSource, userHasInteracted, filterImages]);

  // Handle search input change
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchTerm(value);

      // If search is cleared, reset to initial images and restore pre-selected images
      if (!value.trim()) {
        const filteredInitialImages = filterImages(initialImagesRef.current);

        setImages(filteredInitialImages);
        setHasMore(initialHasMoreRef.current);
        setNextCursor(initialCursorRef.current);
        setIsSearching(false);

        // Only reset to preselected if user hasn't made their own selections
        if (!userHasInteracted) {
          // Restore pre-selected images that are found in filtered initial images
          const foundPreSelected = (preSelectedImagesRef.current || []).filter(
            (preSelected) =>
              filteredInitialImages.some(
                (img) => img.public_id === preSelected.public_id
              )
          );
          setSelectedImages(foundPreSelected);
        } else {
          // Keep current user selections that are still available
          setSelectedImages((prev) =>
            prev.filter((selected) =>
              filteredInitialImages.some(
                (img) => img.public_id === selected.public_id
              )
            )
          );
        }
      }
    },
    [filterImages, userHasInteracted]
  );

  const uploadFiles = async (files: FileList | File[]) => {
    if (!files || files.length === 0) return;

    setIsUploading(true);
    const fileArray = Array.from(files);
    let successCount = 0;
    let duplicateCount = 0;
    let errorCount = 0;

    const uploadPromises = fileArray.map(async (file) => {
      // Check if this file type is allowed based on our filters
      const fileExtension = file.name.split(".").pop()?.toLowerCase() || "";

      // If we have file type filters, use them to determine if file is allowed
      let isAllowed = true;
      if (allowedFileTypes && allowedFileTypes.length > 0) {
        isAllowed = allowedFileTypes.includes(fileExtension);
      } else if (excludeFileTypes && excludeFileTypes.length > 0) {
        isAllowed = !excludeFileTypes.includes(fileExtension);
      } else {
        // Default behavior: only allow image files if no filters specified
        isAllowed = file.type.startsWith("image/");
      }

      if (!isAllowed) {
        errorCount++;
        return null;
      }

      const formData = new FormData();
      formData.append("file", file);

      const uploadHandler = getUploadHandlerForFile(file);
      if (!uploadHandler) {
        errorCount++;
        return null;
      }

      const result = await uploadHandler(formData);

      if (result.success && result.image) {
        successCount++;
        return result.image;
      } else {
        // Count different error types (don't show individual toasts)
        if (result.errorType === "DUPLICATE") {
          duplicateCount++;
        } else if (result.errorType === "INVALID_FILE") {
          errorCount++;
        } else {
          errorCount++;
        }
        return null;
      }
    });

    try {
      const results = await Promise.all(uploadPromises);
      const successfulUploads = results.filter(
        (result): result is CloudinaryImage => result !== null
      );

      if (successfulUploads.length > 0) {
        setImages((prev) => {
          // Filter out any duplicates that might already exist in state
          const newImages = successfulUploads.filter(
            (newImage) =>
              !prev.some(
                (existingImage) =>
                  existingImage.public_id === newImage.public_id
              )
          );
          return [...newImages, ...prev];
        });
      }

      // Show summary toast - only one notification per upload session
      if (fileArray.length === 1) {
        // Single file upload - show specific message
        if (successCount === 1) {
          toast({
            variant: "success",
            description: excludeFileTypesRef.current?.includes("jpg")
              ? "File uploaded successfully"
              : "Image uploaded successfully",
          });
        } else if (duplicateCount === 1) {
          toast({
            variant: "destructive",
            description: `${fileArray[0].name} already exists. Please rename and try again.`,
          });
        } else if (errorCount === 1) {
          toast({
            variant: "destructive",
            description: `Failed to upload ${fileArray[0].name}`,
          });
        }
      } else {
        // Multiple files - show summary
        const messages = [];
        if (successCount > 0) {
          messages.push(`${successCount} uploaded`);
        }
        if (duplicateCount > 0) {
          messages.push(
            `${duplicateCount} duplicate${duplicateCount > 1 ? "s" : ""}`
          );
        }
        if (errorCount > 0) {
          messages.push(`${errorCount} failed`);
        }

        if (messages.length > 0) {
          if (successCount > 0 && (duplicateCount > 0 || errorCount > 0)) {
            toast({
              variant: "success",
              description: `Upload complete: ${messages.join(", ")}`,
            });
          } else if (successCount > 0) {
            toast({
              variant: "success",
              description: `Successfully uploaded ${successCount} ${excludeFileTypesRef.current?.includes("jpg") ? "file(s)" : "image(s)"}`,
            });
          } else {
            toast({
              variant: "destructive",
              description: `Upload failed: ${messages.join(", ")}`,
            });
          }
        }
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast({ variant: "destructive", description: "Upload failed" });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      await uploadFiles(files);
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files) {
      await uploadFiles(files);
    }
  };

  const handleDelete = async (publicId: string) => {
    try {
      const success = await onDelete(publicId);
      if (success) {
        setImages((prev) => prev.filter((img) => img.public_id !== publicId));
        setSelectedImages((prev) =>
          prev.filter((img) => img.public_id !== publicId)
        );
        toast({
          variant: "success",
          description: excludeFileTypesRef.current?.includes("jpg")
            ? "File deleted successfully"
            : "Image deleted successfully",
        });
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast({
        variant: "destructive",
        description: excludeFileTypesRef.current?.includes("jpg")
          ? "Failed to delete file"
          : "Failed to delete image",
      });
    }
  };

  const handleRename = async (publicId: string) => {
    if (!newName.trim()) return;

    try {
      const renamedImage = await onRename(
        publicId,
        `${currentSource.folder}/${newName}`
      );
      setImages((prev) =>
        prev.map((img) => (img.public_id === publicId ? renamedImage : img))
      );
      setSelectedImages((prev) =>
        prev.map((img) => (img.public_id === publicId ? renamedImage : img))
      );
      setEditingId(null);
      setNewName("");
      toast({
        variant: "success",
        description: excludeFileTypesRef.current?.includes("jpg")
          ? "File renamed successfully"
          : "Image renamed successfully",
      });
    } catch (error) {
      console.error("Rename error:", error);
      toast({
        variant: "destructive",
        description: excludeFileTypesRef.current?.includes("jpg")
          ? "Failed to rename file"
          : "Failed to rename image",
      });
    }
  };

  const handleImageSelect = (image: CloudinaryImage) => {
    setUserHasInteracted(true); // Mark that user has made a selection
    setSelectedImages((prev) => {
      const isSelected = prev.some((img) => img.public_id === image.public_id);
      if (isSelected) {
        // Always allow deselecting
        const newSelection = prev.filter(
          (img) => img.public_id !== image.public_id
        );
        return newSelection;
      } else {
        // Check if we've reached the selection limit
        if (maxSelection && prev.length >= maxSelection) {
          if (maxSelection === 1) {
            // For single selection, replace the current selection
            return [image];
          } else {
            // For multiple selection, don't add if limit reached
            toast({
              variant: "destructive",
              description: `Maximum ${maxSelection} ${excludeFileTypesRef.current?.includes("jpg") ? "files" : "images"} can be selected`,
            });
            return prev;
          }
        }
        const newSelection = [...prev, image];
        return newSelection;
      }
    });
  };

  const loadMoreImages = useCallback(async () => {
    if (!hasMore || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const result = await currentSource.onLoadMore(nextCursor, searchTerm);

      const filteredNewImages = filterImages(result.images);

      setImages((prev) => [...prev, ...filteredNewImages]);
      setNextCursor(result.nextCursor);
      setHasMore(result.hasMore);
    } catch (error) {
      console.error("Load more error:", error);
      toast({
        variant: "destructive",
        description: excludeFileTypesRef.current?.includes("jpg")
          ? "Failed to load more files"
          : "Failed to load more images",
      });
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    hasMore,
    isLoadingMore,
    nextCursor,
    currentSource,
    searchTerm,
    filterImages,
  ]);

  // Infinite scroll
  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      if (scrollHeight - scrollTop <= clientHeight * 1.5) {
        loadMoreImages();
      }
    },
    [loadMoreImages]
  );

  const handleSelect = () => {
    onSelect(selectedImages);
    onClose();
  };

  const handleCancel = () => {
    // Reset selections back to original preselected images on cancel
    setSelectedImages(preSelectedImages || []);
    setEditingId(null);
    setUserHasInteracted(false); // Reset interaction flag
    onClose();
  };

  // Drag and drop handlers for reordering selected images
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOverReorder = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeaveReorder = () => {
    setDragOverIndex(null);
  };

  const handleDropReorder = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null) return;

    const newSelectedImages = [...selectedImages];
    const draggedImage = newSelectedImages[draggedIndex];

    // Remove the dragged item
    newSelectedImages.splice(draggedIndex, 1);

    // Insert at new position
    newSelectedImages.splice(dropIndex, 0, draggedImage);

    setSelectedImages(newSelectedImages);
    onSelect(newSelectedImages); // Update parent immediately
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const removeSelectedImage = (index: number) => {
    const newSelectedImages = selectedImages.filter((_, i) => i !== index);
    setSelectedImages(newSelectedImages);
    onSelect(newSelectedImages); // Update parent immediately
  };

  return (
    <>
      {/* Selected Images Display - shown when dialog is closed */}
      {showSelectedDisplay && !isOpen && selectedImages.length > 0 && (
        <div className='space-y-2'>
          <div className='text-sm font-medium text-muted-foreground'>
            {excludeFileTypesRef.current?.includes("jpg")
              ? `Selected Files (${selectedImages.length})`
              : `Selected Images (${selectedImages.length})`}
          </div>
          <div className='flex flex-wrap gap-2'>
            {selectedImages.map((image, index) => (
              <div
                key={image.public_id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOverReorder(e, index)}
                onDragLeave={handleDragLeaveReorder}
                onDrop={(e) => handleDropReorder(e, index)}
                className={`relative group cursor-move border-2 rounded-lg overflow-hidden transition-all w-20 h-20 ${
                  dragOverIndex === index
                    ? "border-primary bg-primary/10"
                    : "border-muted hover:border-muted-foreground"
                }`}
              >
                {isImageFormat(image.format) ? (
                  <Image
                    src={image.secure_url}
                    alt={image.display_name || "Selected image"}
                    fill
                    className='object-cover'
                    sizes='80px'
                  />
                ) : (
                  <div className='w-full h-full flex items-center justify-center p-1'>
                    {(() => {
                      const LogoComponent = getFileTypeLogo(image.format);
                      return LogoComponent ? (
                        <LogoComponent
                          width='100%'
                          height='100%'
                          className='max-w-full max-h-full object-contain'
                          alt={`${image.format.toUpperCase()} file`}
                        />
                      ) : (
                        <div className='text-xs font-bold text-muted-foreground bg-muted rounded w-full h-full flex items-center justify-center'>
                          {image.format?.toUpperCase() || "FILE"}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Index indicator */}
                <div className='absolute top-1 left-1 bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold'>
                  {index + 1}
                </div>

                {/* Remove button */}
                <Button
                  size='sm'
                  variant='destructive'
                  className='absolute top-1 right-1 h-5 w-5 p-0 opacity-0 group-hover:opacity-100 transition-opacity'
                  onClick={() => removeSelectedImage(index)}
                >
                  <X className='h-3 w-3' />
                </Button>
              </div>
            ))}
          </div>
          <div className='text-xs text-muted-foreground'>
            {excludeFileTypesRef.current?.includes("jpg")
              ? "Drag files to reorder them"
              : "Drag images to reorder them"}
          </div>
        </div>
      )}

      <Dialog open={isOpen} onOpenChange={handleCancel}>
        <DialogContent className='max-h-[90vh] p-0'>
          <DialogHeader className='p-6 pb-0 mt-4'>
            <DialogTitle className='flex items-center justify-between'>
              <span className='text-lg'>
                {excludeFileTypesRef.current?.includes("jpg")
                  ? "File Library"
                  : "Media Library"}
              </span>
              <div className='flex items-center gap-2'>
                <Badge variant='secondary'>
                  {selectedImages.length}
                  {maxSelection ? ` / ${maxSelection}` : ""} selected
                </Badge>
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  size='sm'
                  className='flex items-center gap-2'
                >
                  {isUploading ? (
                    <Loader2 className='h-4 w-4 animate-spin' />
                  ) : (
                    <Upload className='h-4 w-4' />
                  )}
                  Upload
                </Button>
              </div>
            </DialogTitle>

            {/* Search Input */}
            <div className='relative mt-4'>
              <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4' />
              <Input
                type='text'
                placeholder={
                  excludeFileTypesRef.current?.includes("jpg")
                    ? "Search files..."
                    : "Search images..."
                }
                value={searchTerm}
                onChange={handleSearchChange}
                className='pl-10 pr-4'
              />
              {isSearching && (
                <Loader2 className='absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin text-gray-400' />
              )}
            </div>
          </DialogHeader>

          <div
            ref={dropZoneRef}
            className={`relative ${
              isDragOver ? "bg-blue-50 border-blue-300" : ""
            } transition-colors`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {/* Drag overlay */}
            {isDragOver && (
              <div className='absolute inset-0 z-10 flex items-center justify-center bg-blue-50 bg-opacity-90 border-2 border-dashed border-blue-300 m-6 rounded-lg'>
                <div className='text-center'>
                  <Upload className='mx-auto h-12 w-12 text-blue-500 mb-4' />
                  <p className='text-lg font-medium text-blue-700'>
                    {excludeFileTypesRef.current?.includes("jpg")
                      ? "Drop files here to upload"
                      : "Drop images here to upload"}
                  </p>
                  <p className='text-sm text-blue-600'>
                    Multiple files supported
                  </p>
                </div>
              </div>
            )}

            <ScrollArea
              className='h-[500px] p-6'
              ref={scrollAreaRef}
              onScrollCapture={handleScroll}
            >
              <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-2'>
                {images.map((image) => (
                  <div
                    key={image.public_id}
                    className={`relative group cursor-pointer border-2 rounded-lg overflow-hidden transition-all ${
                      selectedImages.some(
                        (img) => img.public_id === image.public_id
                      )
                        ? "border-primary"
                        : "border-muted hover:border-muted-foreground"
                    }`}
                    onClick={() => {
                      handleImageSelect(image);
                    }}
                  >
                    <div className='aspect-square relative '>
                      {isImageFormat(image.format) ? (
                        <Image
                          src={image.secure_url}
                          alt={image.display_name || "Image"}
                          fill
                          className='object-contain p-1'
                          sizes='(max-width: 768px) 50vw, 33vw'
                        />
                      ) : (
                        <div className='w-full h-full flex items-center justify-center p-4'>
                          {(() => {
                            const LogoComponent = getFileTypeLogo(image.format);
                            return LogoComponent ? (
                              <LogoComponent
                                width='100%'
                                height='100%'
                                className='max-w-full max-h-full object-contain'
                                alt={`${image.format.toUpperCase()} file`}
                              />
                            ) : (
                              <div className='text-4xl font-bold text-muted-foreground bg-muted rounded-lg w-full h-full flex items-center justify-center'>
                                {image.format?.toUpperCase() || "FILE"}
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      {/* Selection check mark in top left */}
                      {selectedImages.some(
                        (img) => img.public_id === image.public_id
                      ) && (
                        <div className='absolute top-2 left-2 bg-primary rounded-full p-1'>
                          <Check className='h-3 w-3 text-primary-foreground' />
                        </div>
                      )}

                      {/* Action buttons */}
                      <div className='absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1'>
                        <Button
                          size='sm'
                          variant='destructive'
                          className='h-6 w-6 p-0'
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(image.public_id);
                          }}
                        >
                          <Trash2 className='h-3 w-3' />
                        </Button>
                        <Button
                          size='sm'
                          variant='secondary'
                          className='h-6 w-6 p-0'
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingId(image.public_id);
                            setNewName(image.display_name || "");
                          }}
                        >
                          <Edit3 className='h-3 w-3' />
                        </Button>
                      </div>
                    </div>

                    {/* Image name */}
                    <div className='p-1'>
                      {editingId === image.public_id ? (
                        <div className='flex items-center gap-1'>
                          <Input
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            className='h-6 text-xs'
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                handleRename(image.public_id);
                              } else if (e.key === "Escape") {
                                setEditingId(null);
                                setNewName("");
                              }
                            }}
                            autoFocus
                          />
                          <Button
                            size='sm'
                            className='h-6 w-6 p-0'
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRename(image.public_id);
                            }}
                          >
                            <Check className='h-3 w-3' />
                          </Button>
                          <Button
                            size='sm'
                            variant='ghost'
                            className='h-6 w-6 p-0'
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingId(null);
                              setNewName("");
                            }}
                          >
                            <X className='h-3 w-3' />
                          </Button>
                        </div>
                      ) : (
                        <div className='space-y-1'>
                          <p className='text-xs truncate font-medium'>
                            {image.display_name}
                          </p>
                          <p className='text-xs text-gray-500 uppercase'>
                            .{image.format}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Loading more indicator */}
              {isLoadingMore && (
                <div className='flex justify-center mt-4'>
                  <Loader2 className='h-6 w-6 animate-spin' />
                </div>
              )}

              {/* No more images message */}
              {!hasMore && images.length > 0 && (
                <div className='text-center text-gray-500 mt-4'>
                  {excludeFileTypesRef.current?.includes("jpg")
                    ? "No more files to load"
                    : "No more images to load"}
                </div>
              )}

              {/* No files message */}
              {images.length === 0 && !isUploading && !isLoadingMore && (
                <div className='text-center text-gray-500 py-8'>
                  <div className='mb-4'>
                    <Upload className='mx-auto h-12 w-12 text-gray-400 mb-2' />
                    <p className='text-lg font-medium'>
                      {excludeFileTypesRef.current?.includes("jpg")
                        ? "No files found"
                        : "No images found"}
                    </p>
                    <p className='text-sm text-muted-foreground mt-2'>
                      {excludeFileTypesRef.current?.includes("jpg")
                        ? "Upload PDFs, documents, or design files"
                        : "Upload images to get started"}
                    </p>
                  </div>
                  <p className='mb-4'>
                    {excludeFileTypesRef.current?.includes("jpg")
                      ? "Drag & drop multiple files here or click Upload button"
                      : "Drag & drop multiple images here or click Upload button"}
                  </p>
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    variant='outline'
                  >
                    <Upload className='h-4 w-4 mr-2' />
                    Choose Files
                  </Button>
                </div>
              )}
            </ScrollArea>
          </div>

          <DialogFooter className='p-6 pt-0'>
            <div className='flex justify-between w-full'>
              <Button
                variant='outline'
                onClick={() => setSelectedImages([])}
                disabled={selectedImages.length === 0}
              >
                Clear Selection
              </Button>
              <div className='flex gap-2'>
                <Button variant='outline' onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSelect}
                  disabled={selectedImages.length === 0}
                >
                  Select ({selectedImages.length}
                  {maxSelection === 1
                    ? ""
                    : maxSelection
                      ? ` / ${maxSelection}`
                      : ""}
                  )
                </Button>
              </div>
            </div>
          </DialogFooter>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type='file'
            accept={
              allowedFileTypesRef.current?.some((type) =>
                ["pdf", "ai", "eps", "psd", "indd", "doc", "docx"].includes(
                  type
                )
              )
                ? // For non-image dialogs (PDFs, design files)
                  ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.ai,.eps,.psd,.indd,.cdr,.svg"
                : excludeFileTypesRef.current?.includes("jpg")
                  ? // For non-image dialogs (PDFs, design files) - fallback
                    ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.ai,.eps,.psd,.indd,.cdr,.svg"
                  : // For image dialogs
                    "image/*"
            }
            multiple
            className='hidden'
            onChange={handleUpload}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
